/**
 * A `data-follow-ups` part: what the reply left for later to keep the case
 * consistent (a draft citing a removed page, a stale packet, the same fact on
 * another of the client's cases). Collapsed to a count; opening it loads the
 * items still open, grouped by case when there is more than one.
 */
import { ChevronRight, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState, type FC } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import type { FollowUpsData } from "@/features/agents/types";
import { cn } from "@/lib/utils";

import {
  CaseIntegrityAPI,
  type FollowUpGroup,
  type FollowUpItem
} from "@/api/case-integrity";
import { useOfferActionsContext } from "../offers/OfferActionsContext";

type Props = {
  data: FollowUpsData;
  /** The reply the items were left by (the server's turn id). */
  messageId: string;
  className?: string;
};

export const followUpsCountText = (count: number): string =>
  `${count} follow-up${count === 1 ? "" : "s"} left for later`;

const caseTitle = (group: FollowUpGroup): string =>
  group.own
    ? "This case"
    : [group.visa, group.applicant].filter(Boolean).join(" · ") ||
      "Another case";

/** The message "Fix" sends: names the item so the agent can find it. */
export const fixPrompt = (item: FollowUpItem, group: FollowUpGroup): string =>
  `Fix this follow-up${group.own ? "" : ` on ${caseTitle(group)}`}: ${
    item.subject
  } (${item.cause})`;

export const FollowUpsList: FC<Props> = ({ data, messageId, className }) => {
  const actions = useOfferActionsContext();
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState<FollowUpGroup[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [dismissing, setDismissing] = useState<string | null>(null);

  useEffect(() => {
    if (!open || groups !== null) return undefined;
    let cancelled = false;
    CaseIntegrityAPI.getFollowUps(data.individual_id, {
      threadId: data.thread_id,
      messageId
    })
      .then((next) => {
        if (!cancelled) setGroups(next);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, groups, data.individual_id, data.thread_id, messageId]);

  const dismiss = useCallback(
    async (group: FollowUpGroup, item: FollowUpItem) => {
      setDismissing(item.id);
      try {
        await CaseIntegrityAPI.dismiss(group.individual_id, item.id);
        setGroups(
          (current) =>
            current
              ?.map((g) =>
                g.individual_id === group.individual_id
                  ? { ...g, items: g.items.filter((i) => i.id !== item.id) }
                  : g
              )
              .filter((g) => g.items.length) ?? null
        );
      } catch {
        toast.error("Couldn't dismiss that follow-up");
      } finally {
        setDismissing(null);
      }
    },
    []
  );

  const remaining =
    groups?.reduce((total, g) => total + g.items.length, 0) ?? data.count;
  if (groups !== null && remaining === 0) {
    return (
      <div
        className="text-xs text-muted-foreground"
        data-testid="follow-ups-done"
      >
        No follow-ups left.
      </div>
    );
  }
  const grouped = (groups?.length ?? 0) > 1;
  const canFix = Boolean(actions?.say) && !actions?.busy;

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className={cn(
        "rounded-lg border bg-background px-2.5 py-1.5 text-xs",
        className
      )}
      data-testid="follow-ups"
    >
      <CollapsibleTrigger className="flex w-full items-center gap-1 text-left text-muted-foreground hover:text-foreground">
        <ChevronRight
          className={cn("size-3 transition-transform", open && "rotate-90")}
        />
        <span data-testid="follow-ups-count">
          {followUpsCountText(remaining)}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        {failed ? (
          <div className="pt-1 text-xs text-muted-foreground">
            Couldn&apos;t load the follow-ups.
          </div>
        ) : groups === null ? (
          <div className="flex items-center gap-1.5 pt-1 text-xs text-muted-foreground">
            <Loader2 className="size-3 animate-spin" /> Loading…
          </div>
        ) : (
          groups.map((group) => (
            <div key={group.individual_id} className="pt-1">
              {grouped ? (
                <div className="pb-0.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {caseTitle(group)}
                </div>
              ) : null}
              <ul className="divide-y text-xs">
                {group.items.map((item) => (
                  <li
                    key={item.id}
                    className="flex items-start gap-2 py-1"
                    data-testid="follow-up-row"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-foreground">
                        <span className="font-medium">{item.subject}</span>
                        {": "}
                        {item.cause}
                      </div>
                      {item.reason ? (
                        <div className="text-muted-foreground">
                          {item.reason}
                        </div>
                      ) : null}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-xs"
                      disabled={dismissing === item.id}
                      onClick={() => void dismiss(group, item)}
                      data-testid="follow-up-dismiss"
                    >
                      Looks right
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-xs"
                      disabled={!canFix}
                      onClick={() => actions?.say?.(fixPrompt(item, group))}
                      data-testid="follow-up-fix"
                    >
                      Fix
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};
